import { Alert, Button, Stack, Tooltip } from '@mui/material'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { formatTemplateVersion, NO_UNIT_STATE, TEMPLATE_LINK_ACTION } from './templateLinks'

/** Why Update and Dismiss are disabled outside edit mode (adjustment 2026-10-29). */
const NOT_EDITING_TOOLTIP = 'Edit the workflow to update or dismiss the template notice'

/**
 * TemplateUpdateBanner — story 224 (S107, DL8, CU5): the persistent notice that a unit has a newer version of its template.
 * One row per unit with a notice, in the order of `links`. Above the canvas it speaks only for the workflow being viewed
 * (`viewedUnitLinks`); a subworkflow's own notice is shown in the General Properties of its node, as operations do.
 *
 * Adjustment 2026-10-29: an accepted update is a PENDING change that the window's Save writes (like an operation-library update).
 * The row of a unit with a pending update says so ("update ... pending, save to apply") and only offers "View changes", which
 * reopens the impact the user accepted in view-only mode; it no longer offers Update or Dismiss. Outside edit mode Update and
 * Dismiss are shown disabled with an explanation ("View changes" stays enabled).
 *
 * Deliberately NOT built on `AlertUI` (it hides itself after 5 s) — same reasoning as `BomUpdateBanner`.
 *
 * @param {object} props
 * @param {object[]} props.links `TemplateLinkView[]` of E1 (contrato-sun.md §2)
 * @param {string} props.testId `data-testid` of the notice, so the canvas one and the node one can be told apart
 * @param {boolean} props.canAct Whether the user may save workflows (RN-G3, H4): without it the rows carry no Update or Dismiss
 * @param {boolean} props.editing Whether the (top-level) editor is in edit mode: outside it Update and Dismiss are disabled
 * @param {function(object): {pending: object, decision: object}} props.unitStateOf the pending update and the pending decision (HU-05,
 * publish or unlink on save) of a link's unit. A unit with a decision offers neither Update nor Dismiss (RN-07.5)
 * @param {function(object)} props.onUpdate Update of a row (opens the impact dialog)
 * @param {function(object)} props.onDismiss Dismiss of a row (E2)
 * @param {function(object)} props.onViewPending "View changes" of a pending row (the impact dialog, view-only)
 */
const TemplateUpdateBanner = ({
  links = [],
  canAct = false,
  editing = false,
  unitStateOf = () => NO_UNIT_STATE,
  onUpdate = () => {},
  onDismiss = () => {},
  onViewPending = () => {},
  testId = 'template-update-banner'
}) => {
  const { t_ } = useSmartTranslation()
  // A unit with a pending update keeps its row even if a reload of E1 no longer offers the update: the intent is still there.
  const rows = links
    .map((link) => ({ link, state: { ...NO_UNIT_STATE, ...unitStateOf(link) } }))
    .filter(({ link, state }) => link.updateAvailable === true || Boolean(state.pending.scope) || Boolean(state.decision.scope))
  if (rows.length === 0) {
    return null
  }

  const where = (link) => link.scope === 'ROOT' ? t_('Whole workflow') : link.nodePath.join(' / ')

  return (
    <Alert severity='info' data-testid={testId} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
      <Stack spacing={1}>
        {rows.map(({ link, state }) => (
          <Stack
            key={link.scope + ':' + link.subworkflowNodeId}
            direction='row'
            spacing={2}
            alignItems='center'
            justifyContent='space-between'
            data-testid='template-update-row'
            data-pending={state.pending.scope ? 'true' : 'false'}
            data-decision={state.decision.scope ? state.decision.action : ''}
          >
            {state.pending.scope || state.decision.scope
              ? (
                <>
                  <Stack spacing={0.5}>
                    {state.pending.scope
                      ? (
                        <span data-testid='template-update-pending'>
                          {where(link) + ' · ' + t_('Template {{code}}: update v{{from}} → v{{to}} pending, save to apply', {
                            code: link.templateCode,
                            from: formatTemplateVersion(link.templateVersion),
                            to: formatTemplateVersion(state.pending.targetTemplateVersion)
                          })}
                        </span>
                        )
                      : null}
                    {state.decision.scope
                      ? (
                        <span data-testid='template-decision-pending'>
                          {where(link) + ' · ' + t_(state.decision.action === TEMPLATE_LINK_ACTION.PUBLISH
                            ? 'Template {{code}}: will be published as a new version on save'
                            : 'Template {{code}}: will be unlinked on save', { code: link.templateCode })}
                        </span>
                        )
                      : null}
                  </Stack>
                  {state.pending.scope
                    ? (
                      <Button variant='outlined' size='small' data-testid='template-update-view-changes' onClick={() => onViewPending(link)}>
                        {t_('View changes')}
                      </Button>
                      )
                    : null}
                </>
                )
              : (
                <>
                  <span>
                    {where(link) + ' · ' + t_('Template {{code}}: v{{from}} → v{{to}}', {
                      code: link.templateCode,
                      from: formatTemplateVersion(link.templateVersion),
                      to: formatTemplateVersion(link.latestApprovedTemplateVersion)
                    })}
                  </span>
                  {canAct
                    ? (
                      <Stack direction='row' spacing={1}>
                        <Tooltip title={!editing ? t_(NOT_EDITING_TOOLTIP) : ''}>
                          <span>
                            <Button variant='outlined' size='small' disabled={!editing} onClick={() => onUpdate(link)}>
                              {t_('Update')}
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title={!editing ? t_(NOT_EDITING_TOOLTIP) : ''}>
                          <span>
                            <Button variant='outlined' size='small' color='inherit' disabled={!editing} onClick={() => onDismiss(link)}>
                              {t_('Dismiss')}
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                      )
                    : null}
                </>
                )}
          </Stack>
        ))}
      </Stack>
    </Alert>
  )
}

export default TemplateUpdateBanner
